import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Image as ImageIcon,
  PlayCircle,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import {
  acceptAllCorrections,
  getBook,
  getChecks,
  listBookQuestions,
  publishQuestions,
  retryImport,
  type Band,
  type Book,
  type BookQuestion,
  type CheckInfo,
} from "../../api/pqx";
import { useModal } from "../../components/ModalProvider";
import { MathText } from "../../components/MathText";
import { CancelBookButton, RemoveBookButton } from "./RemoveBookButton";
import { RerunBookButton, rerunSummary } from "./RerunBookButton";
import {
  BAND_CLASSES,
  BAND_LABELS,
  IMAGE_BADGES,
  imageStatus,
  BOOK_STATE_CLASSES,
  RUNNING_STATES,
  bookStateLabel,
  checkPass,
  queueLabel,
} from "./bookStates";

const POLL_MS = 5000;

/** One book: extraction progress, then its imported questions to review and publish. */
export function BookScreen() {
  const { bookId = "" } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState<Book | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);

  useEffect(() => {
    let active = true;
    getBook(bookId)
      .then((b) => active && setBook(b))
      .catch((e) => active && setError(e instanceof Error ? e.message : "Couldn't load the book."));
    return () => {
      active = false;
    };
  }, [bookId, tick]);

  // Poll while the engine is still working; a page half takes tens of seconds.
  useEffect(() => {
    if (!book || !RUNNING_STATES.has(book.state)) return;
    const timer = setTimeout(refresh, POLL_MS);
    return () => clearTimeout(timer);
  }, [book]);

  if (error) return <p className="text-negative">{error}</p>;
  if (!book) return <p className="text-text-muted">Loading…</p>;

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/books"
        className="inline-flex items-center gap-1 text-[12px] text-text-muted hover:text-text"
      >
        <ArrowLeft size={14} aria-hidden="true" /> Books
      </Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-[18px] font-bold lg:text-[22px]">{book.filename}</h1>
          <p className="mt-1 text-[13px] text-text-muted">
            {[book.subject?.name, book.exam_type, book.reviewer_name].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-[12px] font-semibold ${BOOK_STATE_CLASSES[book.state] ?? "bg-surface-high text-text"}`}
          >
            {bookStateLabel(book.state)}
          </span>
          <CancelBookButton book={book} onCancelled={setBook} />
          <RemoveBookButton book={book} onRemoved={() => navigate("/books")} />
        </div>
      </div>
      {/* Its own row: the rerun panel needs room for its page box. */}
      <div className="mt-3 flex flex-wrap justify-end">
        <RerunBookButton book={book} onRerun={setBook} />
      </div>
      {book.rerun ? (
        <p role="status" className="mt-2 text-[12px] text-text-muted">
          {rerunSummary(book.rerun)}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-5">
        {RUNNING_STATES.has(book.state) ? <Progress book={book} /> : null}
        {book.state === "failed" || book.state === "import_failed" ? (
          <Failed book={book} onChange={setBook} />
        ) : null}
        {book.verdict === "FAIL" || book.state === "gate_failed" ? (
          <HeldNotice book={book} />
        ) : null}
        {book.state === "imported" && book.mode === "fix" ? (
          <Corrections book={book} />
        ) : book.state === "imported" || book.early_review ? (
          <Imported book={book} onChange={setBook} />
        ) : null}
        {book.state === "cancelled" ? (
          <Panel title="Cancelled">
            <p className="text-[13px] text-text-muted">
              {book.note ?? "This book was cancelled."} It was not extracted, and nothing was
              imported. Upload the file again to start over, or remove it from the list.
            </p>
          </Panel>
        ) : null}
        <Problems book={book} />
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="text-[14px] font-semibold">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Progress({ book }: { book: Book }) {
  const percent = book.progress?.percent ?? 0;
  if (book.state === "queued") {
    const place = queueLabel(book.queue_position);
    return (
      <Panel title="Waiting to start">
        <p className="text-[13px] text-text-muted">
          {place ? `This book is ${place}.` : "This book is in the queue."} The engine extracts a
          few books at a time and starts the rest in the order they were uploaded.
        </p>
        <p className="mt-3 text-[12px] text-text-muted">
          You can leave this page. It starts on its own, and the questions are imported when
          extraction finishes.
        </p>
      </Panel>
    );
  }
  const pass = book.state === "checking_answers" ? checkPass(book.progress) : null;
  const checking = book.state === "checking_answers";
  return (
    <Panel title={checking ? "Checking" : "Extracting"}>
      <div className="h-2 w-full overflow-hidden rounded bg-surface-high" aria-hidden="true">
        <div className="h-full bg-accent transition-[width]" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-[13px] text-text-muted">
        {checking ? (
          <>Pages extracted · {percent}%</>
        ) : (
          <>
            {bookStateLabel(book.state)}
            {book.progress?.detail ? ` — ${book.progress.detail}` : ""} · {percent}%
          </>
        )}
      </p>
      {checking ? (
        // The second layer: the pages are done, and the engine is now going
        // through its checks one pass at a time, each counted from 0.
        <div className="mt-4" data-testid="check-progress">
          <div className="h-2 w-full overflow-hidden rounded bg-surface-high" aria-hidden="true">
            <div
              className="h-full bg-info transition-[width]"
              style={{ width: `${pass?.percent ?? 0}%` }}
            />
          </div>
          <p className="mt-2 text-[13px] text-text-muted">
            {pass
              ? `${pass.step} — ${pass.done} of ${pass.total} · ${pass.percent}%`
              : "Checking answers"}
          </p>
          <p className="mt-1 text-[12px] text-text-muted">
            After the pages, the engine checks each question in a few passes: answers, rereading,
            options and disputes. Each pass has its own count.
          </p>
        </div>
      ) : null}
      {book.counts ? (
        <p className="mt-1 text-[12px] text-text-muted">
          {book.counts.questions} questions so far across {book.counts.years} years.
        </p>
      ) : null}
      <p className="mt-3 text-[12px] text-text-muted">
        You can leave this page. The questions are imported when extraction finishes.
      </p>
    </Panel>
  );
}

function Failed({ book, onChange }: { book: Book; onChange: (b: Book) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      onChange(await retryImport(book.book_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't retry.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title={book.state === "failed" ? "Extraction failed" : "Import failed"}>
      <p className="text-[13px] text-negative">{book.note ?? "No reason was given."}</p>
      {book.state === "failed" ? (
        <p className="mt-2 text-[12px] text-text-muted">
          Nothing was imported. If the engine stopped part-way (a page it couldn't read, a call that
          timed out), <strong>Rerun</strong> above picks up where it left off. If the file itself is
          wrong, check it opens and is the right book, then upload it again.
        </p>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => void retry()}
          className="mt-3 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg disabled:opacity-50"
        >
          {busy ? "Retrying…" : "Retry import"}
        </button>
      )}
      {error ? <p className="mt-2 text-[13px] text-negative">{error}</p> : null}
    </Panel>
  );
}

/**
 * The engine's quality check failed this book. Its questions are imported for
 * review all the same; this says why to look closely. While an old held book
 * waits for the background import, it says so.
 */
function HeldNotice({ book }: { book: Book }) {
  return (
    <section className="rounded-xl border border-warning/40 bg-warning/10 p-5">
      <p className="flex items-center gap-2 text-[14px] font-semibold text-warning">
        <AlertTriangle size={16} aria-hidden="true" /> The engine's quality check failed this book
      </p>
      {book.gates.length ? (
        <ul className="mt-2 list-disc pl-5 text-[13px] text-text-muted">
          {book.gates.map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
      ) : null}
      <p className="mt-2 text-[13px] text-text-muted">
        {book.state === "gate_failed"
          ? "Its questions are being brought in for review now. Refresh in a moment."
          : "Its questions are in the bank for review all the same. Expect more of them to need fixing; none is live until a reviewer approves it."}
      </p>
    </section>
  );
}

function Imported({ book, onChange }: { book: Book; onChange: (b: Book) => void }) {
  const modal = useModal();
  const [items, setItems] = useState<BookQuestion[] | null>(null);
  const [checks, setChecks] = useState<Record<string, CheckInfo>>({});
  const [band, setBand] = useState<Band | "all">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    listBookQuestions(book.book_id)
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
    // `book.state` too: when the engine's checks finish, the questions it was
    // still checking come back with their final values, issues and approval.
  }, [book.book_id, book.state, tick]);

  useEffect(() => {
    getChecks()
      .then((r) => setChecks(Object.fromEntries(r.checks.map((c) => [c.id, c]))))
      .catch(() => setChecks({}));
  }, []);

  const [imagesOnly, setImagesOnly] = useState<"all" | "images" | "missing">("all");
  const shown = useMemo(
    () =>
      (items ?? []).filter((q) => {
        if (band !== "all" && q.band !== band) return false;
        const status = imageStatus(q);
        if (imagesOnly === "images") return status !== "none";
        if (imagesOnly === "missing") return status === "missing";
        return true;
      }),
    [items, band, imagesOnly],
  );
  const withImages = (items ?? []).filter((q) => imageStatus(q) !== "none").length;
  const needingImages = (items ?? []).filter((q) => imageStatus(q) === "missing").length;
  const reviewing = (items ?? []).filter((q) => q.status === "under_review");
  const live = (items ?? []).filter((q) => q.status === "active").length;
  /** In book order, so a reviewer picks up where the last one stopped. */
  const firstPending = reviewing[0]?.id ?? null;
  const counts = (["red", "amber", "green"] as Band[]).map(
    (b) => [b, reviewing.filter((q) => q.band === b).length] as const,
  );

  const publish = async (ids?: string[]) => {
    setBusy(true);
    try {
      const result = await publishQuestions(book.book_id, ids);
      const skipped = result.skipped.length
        ? ` ${result.skipped.length} skipped: they need an answer and at least two options.`
        : "";
      if (result.published) {
        modal.showSuccess(
          `${result.published} published`,
          `They are now live for students.${skipped}`,
        );
      } else {
        modal.showWarning("Nothing published", `No question was ready.${skipped}`);
      }
      setSelected(new Set());
      setTick((t) => t + 1);
      onChange(book);
    } catch (e) {
      modal.showError("Couldn't publish", e instanceof Error ? e.message : "Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const greens = counts.find(([b]) => b === "green")?.[1] ?? 0;

  return (
    <Panel title="Review and publish">
      {book.early_review ? (
        <p role="status" className="mb-3 rounded-lg bg-info/10 px-4 py-3 text-[13px] text-info">
          You can start reviewing now: the engine is still checking this book's answers. Approving
          opens when the checks finish. What you change meanwhile is kept, and where the engine's
          final answer differs, the question says so.
        </p>
      ) : null}
      <p className="text-[13px] text-text-muted">
        {book.imported} questions arrived in the bank as <strong>under review</strong>
        {book.verdict ? ` · engine verdict ${book.verdict}` : ""} · {live} live. Review them one at
        a time to see each question's issues, fix and approve it.
      </p>
      {items && items.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {firstPending ? (
            <Link
              to={`/books/${book.book_id}/review/${firstPending}`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg"
            >
              <PlayCircle size={14} aria-hidden="true" />
              {reviewing.length === items.length ? "Start reviewing" : "Continue reviewing"} (
              {reviewing.length} left)
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-positive">
              <CheckCircle2 size={14} aria-hidden="true" /> Every question reviewed
            </span>
          )}
        </div>
      ) : null}
      {book.import_errors.length ? (
        <details className="mt-3 rounded-lg bg-negative/10 px-4 py-2 text-[12px] text-negative">
          <summary className="cursor-pointer font-semibold">
            {book.import_errors.length} questions could not be imported
          </summary>
          <ul className="mt-2 list-disc pl-5">
            {book.import_errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {(["all", "red", "amber", "green"] as const).map((b) => (
          <button
            key={b}
            type="button"
            aria-pressed={band === b}
            onClick={() => setBand(b)}
            className={`rounded-full px-3 py-1 text-[12px] font-medium ${
              band === b ? "bg-accent text-bg" : "bg-surface-high text-text-muted hover:text-text"
            }`}
          >
            {b === "all"
              ? `All ${items?.length ?? ""}`
              : `${BAND_LABELS[b]} ${counts.find(([x]) => x === b)?.[1] ?? 0}`}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        {(
          [
            ["images", `With images ${withImages}`],
            ["missing", `Image needed ${needingImages}`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={imagesOnly === key}
            onClick={() => setImagesOnly(imagesOnly === key ? "all" : key)}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-medium ${
              imagesOnly === key
                ? "bg-accent text-bg"
                : "bg-surface-high text-text-muted hover:text-text"
            }`}
          >
            <ImageIcon size={12} aria-hidden="true" /> {label}
          </button>
        ))}
        <span className="flex-1" />
        <button
          type="button"
          // Not while the engine's checks run: nothing it's still checking can go live.
          disabled={busy || selected.size === 0 || book.early_review === true}
          onClick={() => void publish([...selected])}
          className="rounded-lg border border-border px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50"
        >
          Publish selected ({selected.size})
        </button>
        <button
          type="button"
          disabled={busy || greens === 0 || book.early_review === true}
          onClick={() => void publish()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-bg disabled:opacity-50"
        >
          <CheckCircle2 size={14} aria-hidden="true" /> Publish all green ({greens})
        </button>
      </div>

      {items === null ? <p className="mt-4 text-text-muted">Loading questions…</p> : null}
      <ul className="mt-4 flex flex-col divide-y divide-border">
        {shown.map((q) => (
          <li key={q.id} className="flex items-start gap-3 py-3">
            <input
              type="checkbox"
              aria-label={`Select ${q.year} question ${q.number}`}
              disabled={q.status !== "under_review"}
              checked={selected.has(q.id)}
              onChange={() => toggle(q.id)}
              className="mt-1"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="font-semibold text-text-muted tabular-nums">{where(q)}</span>
                {q.band ? (
                  <span className={`rounded px-1.5 py-0.5 font-semibold ${BAND_CLASSES[q.band]}`}>
                    {BAND_LABELS[q.band]}
                  </span>
                ) : null}
                {q.status === "active" ? (
                  <span className="rounded bg-positive/15 px-1.5 py-0.5 font-semibold text-positive">
                    Live
                  </span>
                ) : null}
                <ImageBadge status={imageStatus(q)} />
                {q.checks.map((c) => (
                  <span
                    key={c}
                    title={checks[c]?.message}
                    className="rounded bg-surface-high px-1.5 py-0.5 text-text-muted"
                  >
                    {checks[c]?.name ?? c}
                  </span>
                ))}
              </div>
              <MathText text={q.question_text} className="mt-1 block text-[13px] leading-[1.5]" />
              <p className="mt-1 text-[12px] text-text-muted">
                Answer: {q.correct_answer ?? "none"}
                {q.topic ? ` · ${q.topic}` : ""}
              </p>
            </div>
            <Link
              to={`/books/${book.book_id}/review/${q.id}`}
              className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-accent-light"
            >
              Review <ExternalLink size={12} aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** "2023 · Q4" for a book; "Q4" for a checked file with no year. */
const where = (q: BookQuestion) => (q.year ? `${q.year} · Q${q.number}` : `Q${q.number}`);

/**
 * Bank questions the engine checked. Each correction is accepted onto the
 * live question or kept as it is; the plain ones can be accepted together.
 */
function Corrections({ book }: { book: Book }) {
  const modal = useModal();
  const [items, setItems] = useState<BookQuestion[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    listBookQuestions(book.book_id)
      .then((r) => setItems(r.items))
      .catch(() => setItems([]));
  }, [book.book_id, tick]);

  const all = items ?? [];
  const waiting = all.filter((q) => q.proposal && !q.decision);
  /** Corrections the engine made on its own; held ones need a person. */
  const plain = waiting.filter((q) => !q.proposal?.review_reason);
  const accepted = all.filter((q) => q.decision === "accepted").length;
  const unchanged = all.filter((q) => !q.proposal).length;

  const acceptAll = async () => {
    const ok = await modal.showConfirm(
      `Accept ${plain.length} corrections?`,
      "Each question changes now, for students from their next attempt. The ones the engine held for a person are left for you.",
    );
    if (!ok) return;
    setBusy(true);
    try {
      const result = await acceptAllCorrections(book.book_id);
      const skipped = result.skipped.length
        ? ` ${result.skipped.length} left to decide by hand.`
        : "";
      modal.showSuccess(`${result.accepted} corrections accepted`, `They're live now.${skipped}`);
      setTick((t) => t + 1);
    } catch (e) {
      modal.showError("Couldn't accept them", e instanceof Error ? e.message : "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="The engine's corrections">
      <p className="text-[13px] text-text-muted">
        {all.length} questions checked · {unchanged} correct as they were · {accepted} corrections
        accepted · {waiting.length} to decide. Nothing changes on a question until you accept its
        correction.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {waiting[0] ? (
          <Link
            to={`/books/${book.book_id}/review/${waiting[0].id}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-bg"
          >
            <PlayCircle size={14} aria-hidden="true" /> Go through them ({waiting.length} left)
          </Link>
        ) : items ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-positive">
            <CheckCircle2 size={14} aria-hidden="true" /> Every correction decided
          </span>
        ) : null}
        <button
          type="button"
          disabled={busy || plain.length === 0}
          onClick={() => void acceptAll()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-[13px] font-semibold disabled:opacity-50"
        >
          <CheckCircle2 size={14} aria-hidden="true" /> Accept all corrections ({plain.length})
        </button>
      </div>

      {items === null ? <p className="mt-4 text-text-muted">Loading questions…</p> : null}
      <ul className="mt-4 flex flex-col divide-y divide-border">
        {all
          .filter((q) => q.proposal)
          .map((q) => (
            <li key={q.id} className="flex items-start gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  {q.band ? (
                    <span className={`rounded px-1.5 py-0.5 font-semibold ${BAND_CLASSES[q.band]}`}>
                      {BAND_LABELS[q.band]}
                    </span>
                  ) : null}
                  {q.decision === "accepted" ? (
                    <span className="rounded bg-positive/15 px-1.5 py-0.5 font-semibold text-positive">
                      Accepted
                    </span>
                  ) : q.decision === "kept" ? (
                    <span className="rounded bg-surface-high px-1.5 py-0.5 text-text-muted">
                      Kept
                    </span>
                  ) : q.proposal?.review_reason ? (
                    <span className="rounded bg-warning/15 px-1.5 py-0.5 font-semibold text-warning">
                      Needs a person
                    </span>
                  ) : null}
                </div>
                <MathText text={q.question_text} className="mt-1 block text-[13px] leading-[1.5]" />
                <p className="mt-1 text-[12px] text-text-muted">
                  {q.proposal?.review_reason ?? q.proposal?.changes.join(" · ")}
                </p>
              </div>
              <Link
                to={`/books/${book.book_id}/review/${q.id}`}
                className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-accent-light"
              >
                Look <ExternalLink size={12} aria-hidden="true" />
              </Link>
            </li>
          ))}
      </ul>
    </Panel>
  );
}

function Problems({ book }: { book: Book }) {
  if (!book.problems.length) return null;
  return (
    <Panel title={`Engine's hand-check list (${book.problems.length})`}>
      <ul className="list-disc pl-5 text-[13px] text-text-muted">
        {book.problems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </Panel>
  );
}

function ImageBadge({ status }: { status: ReturnType<typeof imageStatus> }) {
  if (status === "none") return null;
  const badge = IMAGE_BADGES[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-semibold ${badge.className}`}
    >
      <ImageIcon size={11} aria-hidden="true" /> {badge.label}
    </span>
  );
}
